import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * CommsUpgradeDialog — shown when a workshop on a tier WITHOUT the `comms`
 * feature (Essential) clicks any SMS / Email action. Buttons stay visible;
 * the click opens this upgrade prompt instead of the send flow.
 */
export function CommsUpgradeDialog({ open, onOpenChange }) {
  const { subscription } = useSubscription();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="comms-upgrade-dialog">
        <div className="text-center space-y-4 py-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="h-7 w-7 text-amber-600" />
          </div>
          <DialogTitle className="text-lg font-bold text-slate-900">SMS &amp; Email are locked</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Your workshop is on the <span className="font-semibold capitalize">{subscription || 'Essential'}</span> plan.
            Upgrade to <span className="font-semibold">Standard or Pro</span> to use SMS &amp; Email.
          </DialogDescription>
          <Button onClick={() => onOpenChange(false)} className="w-full" data-testid="comms-upgrade-close-btn">
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * useCommsGate — wrap any SMS/Email click handler with `guardComms(fn)`.
 * Standard/Pro (and Super Admins) pass straight through; Essential users
 * get the upgrade dialog. Render `commsDialog` once in the component.
 */
export function useCommsGate() {
  const { hasFeature } = useSubscription();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const commsAllowed = hasFeature('comms');
  const guardComms = (fn) => (...args) => {
    if (!commsAllowed) {
      setUpgradeOpen(true);
      return undefined;
    }
    return fn(...args);
  };
  const commsDialog = <CommsUpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />;
  return { commsAllowed, guardComms, commsDialog };
}
